<template>
  <div
    aria-live="polite"
    class="alert"
    :class="`alert-${type} ${alertClass}`"
    role="status"
  >
    <div v-if="!hideIcon">
      <QIcon
        :name="alertIcon"
        :size="iconSize"
      />
    </div>
    <div
      class="q-ml-sm"
      data-testid="content"
    >
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
export const ALERT_ICONS = {
  error: "ion-alert",
  info: "info",
  success: "ion-checkmark-circle-outline",
  warning: "ion-alert",
} as const;
</script>

<script setup lang="ts">
import { computed } from "vue";

export type AlertIconMap = typeof ALERT_ICONS;
export type AlertType = keyof AlertIconMap;
export type AlertIcon = AlertIconMap[AlertType];

export interface Props {
  alertClass?: string;
  hideIcon?: boolean;
  iconSize?: string;
  type?: AlertType;
}

const props = withDefaults(defineProps<Props>(), {
  type: "warning",
  iconSize: "1.5rem",
  alertClass: "tw:p-2.5",
});

const alertIcon = computed<AlertIcon>(() => ALERT_ICONS[props.type]);
</script>

<style lang="scss" scoped>
.alert {
  background-color: $ds-orange-1;
  border: 1px solid $ds-orange-5;
  border-radius: 3px;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  color: $ds-orange-5;

  &-error {
    background-color: $ds-red-1;
    border-color: $ds-red-5;
    color: $ds-red-5;
  }

  &-info {
    background-color: $ds-blue-1;
    border-color: $ds-blue-4;
    color: $ds-blue-6;
  }

  &-success {
    background-color: $ds-green-1;
    border-color: $ds-green-4;
    color: $ds-green-6;
  }

  &-warning {
    background-color: $ds-orange-1;
    border-color: $ds-orange-5;
    color: $ds-orange-5;
  }
}
</style>
